import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, CheckCircle2, FileText, 
  ClipboardCheck, AlertTriangle, Shield, Zap, 
  Clock, AlertCircle, HardHat, Wrench, Users,
  ChevronRight, Truck, Receipt, CreditCard, Trophy, 
  Banknote, Calculator, Share2, Eye, Download, Send,
  Camera, Mic, Upload
} from 'lucide-react';
import RequestSetupModal from '../components/ui/request-setup-modal';
import ShareModal from '../components/ui/share-modal';

// Before/After Mockup Component for SWMS
const BeforeAfterMockup = () => {
  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-between gap-4">
        {/* Before - Scattered Info */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex-1 bg-slate-100 rounded-2xl p-6 border-2 border-slate-200"
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Before 2Tap...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <FileText className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Generic SWMS Template</p>
                <p className="text-xs text-slate-500">Doesn't match the actual job</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Clock className="w-8 h-8 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Hours of Paperwork</p>
                <p className="text-xs text-slate-500">Editing boxes, adding risks...</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Missed Hazards</p>
                <p className="text-xs text-slate-500">Site-specific risks forgotten</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Users className="w-8 h-8 text-slate-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">No Worker Sign-Off</p>
                <p className="text-xs text-slate-500">Chasing signatures on site</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center gap-2"
        >
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <p className="text-xs font-bold text-blue-500 text-center">2TAP<br/>GENERATES</p>
        </motion.div>

        {/* After - 2Tap Output */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex-1 bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border-2 border-blue-200"
        >
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4">2Tap creates...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <ClipboardCheck className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Job-Specific SWMS</p>
                <p className="text-xs text-blue-600">Tailored to your actual work</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <AlertTriangle className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Risk Assessment</p>
                <p className="text-xs text-blue-600">Hazards & controls identified</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <Shield className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">PPE Requirements</p>
                <p className="text-xs text-blue-600">Listed and ready</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <Download className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">PDF Ready to Sign</p>
                <p className="text-xs text-blue-600">Send to crew instantly</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-4">
        <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
          <p className="text-xs font-bold text-slate-500 mb-3">BEFORE</p>
          <div className="flex items-center gap-3">
            <FileText className="w-10 h-10 text-red-500" />
            <div>
              <p className="text-sm font-semibold">Generic templates</p>
              <p className="text-xs text-slate-500">Hours of manual editing</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-200">
          <p className="text-xs font-bold text-blue-600 mb-3">AFTER</p>
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-10 h-10 text-blue-500" />
            <div>
              <p className="text-sm font-semibold">Job-specific SWMS</p>
              <p className="text-xs text-blue-600">Generated in minutes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Example SWMS Output Component
const ExampleSWMSOutput = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      {/* Left - Input */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-slate-100 rounded-2xl p-6"
      >
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Mic className="w-4 h-4" />
          YOU TELL 2TAP
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Voice note transcription:</p>
              <p className="text-sm text-slate-700 italic">
                "Starting at the Smith project tomorrow. We're doing roof work on a two-storey house. 
                Need to set up scaffolding, working at heights obviously. The client mentioned there's 
                asbestos eaves that we need to work around. Weather looks okay but might rain Thursday."
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Camera className="w-4 h-4 text-slate-400" />
            <span>+ Site photos attached</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Upload className="w-4 h-4 text-slate-400" />
            <span>+ Builder scope document</span>
          </div>
        </div>
      </motion.div>

      {/* Right - Output with Tabs */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-white rounded-2xl shadow-lg border border-blue-100 overflow-hidden"
      >
        {/* Tab Header */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'hazards', label: 'Hazards', icon: AlertTriangle },
            { id: 'workers', label: 'Workers & Sign-Off', icon: Users }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-semibold transition-colors ${
                activeTab === tab.id 
                  ? 'text-blue-600 bg-white border-b-2 border-blue-500' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'overview' && (
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
                  <ClipboardCheck className="w-3.5 h-3.5" />
                  SWMS SUMMARY
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="space-y-2 text-xs">
                  <div><span className="text-slate-500">Project:</span> <span className="font-medium">Smith Residence - Roof Works</span></div>
                  <div><span className="text-slate-500">Location:</span> <span className="font-medium">42 Example Street, Suburb</span></div>
                  <div><span className="text-slate-500">Date:</span> <span className="font-medium">Tomorrow + ongoing</span></div>
                  <div><span className="text-slate-500">Trade:</span> <span className="font-medium">Roofing</span></div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">High-Risk Work Identified</p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-red-100 text-red-700 text-[10px] font-semibold px-2 py-1 rounded">Working at Heights</span>
                  <span className="bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-1 rounded">Scaffolding</span>
                  <span className="bg-purple-100 text-purple-700 text-[10px] font-semibold px-2 py-1 rounded">Asbestos Proximity</span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Work Steps</p>
                <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Site induction and toolbox talk</li>
                  <li>Scaffold erection and inspection</li>
                  <li>Establish exclusion zone around asbestos</li>
                  <li>Roof access and fall protection setup</li>
                  <li>Roofing works as per scope</li>
                  <li>Daily cleanup and secure site</li>
                </ol>
              </div>
            </div>
          )}

          {activeTab === 'hazards' && (
            <div className="space-y-3">
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  IDENTIFIED HAZARDS & CONTROLS
                </p>
              </div>

              {[
                { 
                  hazard: 'Falls from Height', 
                  risk: 'High', 
                  controls: ['Scaffolding with guardrails', 'Harness when near edges', 'Secure ladder access'],
                  color: 'red'
                },
                { 
                  hazard: 'Asbestos Exposure', 
                  risk: 'High', 
                  controls: ['Maintain 10m exclusion zone', 'Do not disturb eaves', 'Licensed removal if required'],
                  color: 'purple'
                },
                { 
                  hazard: 'Weather - Rain/Wind', 
                  risk: 'Medium', 
                  controls: ['Monitor BOM forecast', 'Cease work if unsafe', 'Secure materials daily'],
                  color: 'amber'
                },
                { 
                  hazard: 'Manual Handling', 
                  risk: 'Medium', 
                  controls: ['Team lift for heavy items', 'Use mechanical aids', 'Regular breaks'],
                  color: 'blue'
                }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-slate-800">{item.hazard}</p>
                    <span className={`bg-${item.color}-100 text-${item.color}-700 text-[10px] font-semibold px-2 py-0.5 rounded`}>
                      {item.risk} Risk
                    </span>
                  </div>
                  <ul className="text-xs text-slate-600 space-y-0.5">
                    {item.controls.map((control, j) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                        {control}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'workers' && (
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  WORKERS ON SITE & ROLES
                </p>
              </div>

              {/* Workers List */}
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Crew Members</p>
                <div className="space-y-2">
                  {[
                    { name: 'Mike Thompson', role: 'Site Supervisor' },
                    { name: 'Dave Wilson', role: 'Roofer' },
                    { name: 'James Lee', role: 'Apprentice' }
                  ].map((worker, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-xs font-medium text-slate-800">{worker.name}</span>
                      <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{worker.role}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Signature Section */}
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Mobile Signature Capture</p>
                <div className="border-2 border-dashed border-blue-300 rounded-lg p-3 bg-blue-50/50">
                  {/* Simulated signature */}
                  <div className="h-16 flex items-center justify-center">
                    <svg viewBox="0 0 200 50" className="w-32 h-10 text-blue-600">
                      <path 
                        d="M10 35 Q30 10 50 30 T90 25 T130 35 T170 20" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <p className="text-[10px] text-blue-600 text-center mt-1">Draw signature on mobile screen</p>
                </div>
              </div>

              {/* Timestamped Sign-Off */}
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3" />
                  TIMESTAMPED SIGN-OFF
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] text-emerald-600">Signed by</p>
                    <p className="text-xs font-semibold text-emerald-800">Mike Thompson</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-emerald-600">Signed at</p>
                    <p className="text-xs font-semibold text-emerald-800">8 Jul 2026 – 6:45 AM</p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-emerald-200">
                  <div className="flex items-center gap-1.5 text-emerald-600">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">Signature captured & saved</span>
                  </div>
                </div>
              </div>

              {/* PPE Quick List */}
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">PPE Requirements</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Hard Hat', 'Hi-Vis', 'Safety Boots', 'Harness', 'Gloves', 'Glasses'].map((item, i) => (
                    <span key={i} className="bg-green-100 text-green-700 text-[10px] font-medium px-2 py-0.5 rounded">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Action */}
        <div className="p-4 pt-0">
          <div className="bg-blue-500 rounded-lg p-3 text-center cursor-pointer hover:bg-blue-600 transition-colors">
            <p className="text-xs font-bold text-white flex items-center justify-center gap-2">
              Download PDF & Send to Crew
              <Download className="w-4 h-4" />
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Workflow Step Component
const WorkflowStep = ({ icon: Icon, label, isActive, isCompleted }) => (
  <div className={`flex flex-col items-center gap-2 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
      isActive ? 'bg-blue-500 text-white' : 
      isCompleted ? 'bg-green-500 text-white' :
      'bg-slate-200 text-slate-500'
    }`}>
      <Icon className="w-6 h-6" />
    </div>
    <p className={`text-xs font-medium text-center max-w-[80px] ${isActive ? 'text-blue-600' : 'text-slate-600'}`}>
      {label}
    </p>
  </div>
);

// Main Page Component
const FeatureSWMS = () => {
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigation links for related areas
  const relatedAreas = [
    { icon: Calculator, label: 'Scope & Quotes', href: '/features/scope-costing-quote', color: 'emerald' },
    { icon: ClipboardCheck, label: 'SWMS & Safety', href: '/features/swms-safety', color: 'blue', active: true },
    { icon: Truck, label: 'Site Updates', href: '/features/site-updates', color: 'blue' },
    { icon: AlertTriangle, label: 'Variations', href: '/features/variations', color: 'blue' },
    { icon: Receipt, label: 'Invoicing', href: '/features', color: 'orange' },
    { icon: CreditCard, label: 'Payments', href: '/features', color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation - Consistent with Landing Page */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-sm font-medium tracking-wide"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide flex items-center gap-1.5"
            >
              <Share2 className="w-4 h-4" />
              SHARE
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 font-semibold tracking-wide shadow-[3px_3px_0px_rgba(0,0,0,0.2)]"
            >
              LOG IN
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-xs font-medium px-2"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="p-1.5 text-slate-600 hover:text-slate-900"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs px-3"
            >
              LOG IN
            </Button>
          </div>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto">
          {/* Feature Label */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wider"
          >
            <ClipboardCheck className="w-4 h-4" />
            SWMS & SAFETY DOCUMENTS
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Text */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Hook */}
              <p className="text-blue-400 text-sm md:text-base font-semibold mb-3">
                Need a SWMS before starting work?
              </p>
              
              {/* Headline */}
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                COMPLIANT SWMS,<br />
                <span className="text-blue-500">GENERATED IN MINUTES</span>
              </h1>

              {/* Subheading */}
              <p className="text-slate-300 text-sm md:text-base mb-4 leading-relaxed">
                Tell 2Tap about the job — voice, text, or photos. It generates a job-specific 
                Safe Work Method Statement with hazards, controls, and PPE requirements identified.
              </p>

              {/* Supporting Line */}
              <p className="text-slate-400 text-sm border-l-2 border-blue-500 pl-3 mb-8">
                Stop using generic templates. Get site-specific safety docs that actually match the work you're doing.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => setShowSetupModal(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm px-6 py-3 font-bold"
                >
                  REGISTER INTEREST
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Right - Visual Mockup */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="hidden md:block"
            >
              <BeforeAfterMockup />
            </motion.div>
          </div>

          {/* Mobile Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="md:hidden mt-10"
          >
            <BeforeAfterMockup />
          </motion.div>
        </div>
      </section>

      {/* 2. Problem Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 text-red-500 text-xs font-bold tracking-widest mb-4">
              <AlertCircle className="w-4 h-4" />
              THE PROBLEM
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              GENERIC SWMS TEMPLATES<br />
              <span className="text-red-500">DON'T MATCH YOUR JOB</span>
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              You know you need a SWMS, but filling out generic templates takes forever. 
              Site-specific hazards get missed. Builders want docs before you start. 
              It's a compliance headache that slows you down.
            </p>
          </motion.div>

          {/* Pain Points */}
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[
              { icon: Clock, text: 'Hours editing templates' },
              { icon: AlertTriangle, text: 'Site hazards overlooked' },
              { icon: FileText, text: 'Builders chasing paperwork' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <item.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. What 2Tap Creates Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-blue-500 text-xs font-bold tracking-widest mb-4">
              <CheckCircle2 className="w-4 h-4" />
              WHAT 2TAP CREATES
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              JOB-SPECIFIC SWMS<br />
              <span className="text-blue-500">READY TO USE ON SITE</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: ClipboardCheck, 
                title: 'Complete SWMS Document', 
                desc: 'Work steps, hazards, and controls — tailored to your job',
                color: 'blue'
              },
              { 
                icon: AlertTriangle, 
                title: 'Risk Assessment', 
                desc: 'Site-specific hazards with control measures',
                color: 'amber'
              },
              { 
                icon: Users, 
                title: 'Workers & Roles', 
                desc: 'Add crew members with their roles on site',
                color: 'emerald'
              },
              { 
                icon: CheckCircle2, 
                title: 'Mobile Sign-Off', 
                desc: 'Capture signatures on-site with timestamp',
                color: 'green'
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`bg-gradient-to-br from-${item.color}-50 to-white rounded-2xl p-6 border border-${item.color}-100 shadow-sm`}
              >
                <div className={`w-14 h-14 bg-${item.color}-500 rounded-xl flex items-center justify-center mb-4`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. How It Works Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-blue-500 text-xs font-bold tracking-widest mb-4">
              <Zap className="w-4 h-4" />
              HOW IT WORKS
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              THREE STEPS TO<br />
              <span className="text-blue-500">A COMPLIANT SWMS</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              { 
                step: '1', 
                title: 'Describe the Job', 
                desc: 'Tell 2Tap about the work — voice note, text, or upload the scope. Mention the site, tasks, and any known hazards.' 
              },
              { 
                step: '2', 
                title: '2Tap Generates Your SWMS', 
                desc: 'AI identifies hazards, suggests controls, lists PPE requirements, and creates work steps based on your trade and job type.' 
              },
              { 
                step: '3', 
                title: 'Review, Download & Share', 
                desc: 'Check the document, make any tweaks, then download the PDF or send it directly to your crew and the builder.' 
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-black text-xl">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Example Output Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-slate-500 text-xs font-bold tracking-widest mb-4">
              <FileText className="w-4 h-4" />
              EXAMPLE
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              SEE THE<br />
              <span className="text-blue-500">OUTPUT</span>
            </h2>
          </motion.div>

          <ExampleSWMSOutput />
        </div>
      </section>

      {/* 5.5 Mobile Sign-Off Feature Highlight */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-emerald-500 text-xs font-bold tracking-widest mb-4">
              <CheckCircle2 className="w-4 h-4" />
              KEY FEATURE
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              MOBILE SIGN-OFF<br />
              <span className="text-emerald-500">WITH TIMESTAMP</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Feature Description */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Add Workers & Roles</h3>
                  <p className="text-sm text-slate-600">List all crew members on site with their specific roles. Everyone knows who's responsible for what.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 19l7-7 3 3-7 7-3-3z" />
                    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                    <path d="M2 2l7.586 7.586" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Draw Signature on Mobile</h3>
                  <p className="text-sm text-slate-600">Workers sign directly on their phone screen. No paper, no pen, no scanning.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Automatic Timestamp</h3>
                  <p className="text-sm text-slate-600">Every signature is recorded with exact date and time. Proof of who signed, and when.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Saved & Accessible</h3>
                  <p className="text-sm text-slate-600">Signed SWMS is saved to your 2Tap history. Find it anytime, share with builders, keep records.</p>
                </div>
              </div>
            </motion.div>

            {/* Right - Visual Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg border border-emerald-100 p-6"
            >
              <div className="text-center mb-4">
                <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Sign-Off Confirmation</p>
              </div>

              {/* Workers List */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">Workers on Site</p>
                <div className="space-y-2">
                  {[
                    { name: 'Mike Thompson', role: 'Site Supervisor', signed: true },
                    { name: 'Dave Wilson', role: 'Roofer', signed: true },
                    { name: 'James Lee', role: 'Apprentice', signed: false }
                  ].map((worker, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${worker.signed ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                          {worker.signed ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Clock className="w-3 h-3 text-slate-400" />
                          )}
                        </div>
                        <span className="text-xs font-medium text-slate-800">{worker.name}</span>
                      </div>
                      <span className="text-[10px] text-slate-500">{worker.role}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Signature Display */}
              <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50/50 mb-4">
                <div className="h-20 flex items-center justify-center">
                  <svg viewBox="0 0 200 50" className="w-40 h-12 text-slate-700">
                    <path 
                      d="M10 35 Q30 10 50 30 T90 25 T130 35 T170 20 Q180 15 190 25" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Timestamp */}
              <div className="bg-emerald-100 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-[10px] text-emerald-600 font-medium">Signed by</p>
                    <p className="text-sm font-bold text-emerald-800">Mike Thompson</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-emerald-600 font-medium">Timestamp</p>
                    <p className="text-sm font-bold text-emerald-800">8 Jul 2026, 6:45 AM</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-200 text-center">
                  <div className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4" />
                    SWMS Signed & Saved
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. Why It Matters Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHY IT MATTERS
            </h2>
            <p className="text-blue-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Safety documentation isn't just paperwork — it protects you, your crew, and your business.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Stay Compliant', desc: 'Meet WHS requirements without the hassle' },
              { icon: Clock, title: 'Save Hours', desc: 'Generate docs in minutes, not hours' },
              { icon: CheckCircle2, title: 'Site Ready', desc: 'Professional docs that builders accept' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-5"
              >
                <item.icon className="w-10 h-10 text-white mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-blue-100">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. You're In Control Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 rounded-3xl p-8 md:p-12 text-center"
          >
            <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              YOU REVIEW & APPROVE<br />
              <span className="text-blue-500">BEFORE IT GOES OUT</span>
            </h2>
            <p className="text-slate-300 text-sm md:text-base max-w-2xl mx-auto">
              2Tap generates the draft — but you're always in control. Review the hazards, 
              check the controls, add site-specific notes, and approve before sharing. 
              Your name, your responsibility, your final say.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 8. Where It Fits Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
              <Truck className="w-4 h-4" />
              PART OF DELIVER THE WORK
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHERE IT FITS IN<br />
              <span className="text-blue-500">2TAP</span>
            </h2>
          </motion.div>

          {/* Workflow */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm mb-10">
            <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
              <WorkflowStep icon={FileText} label="Job Accepted" isCompleted />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={ClipboardCheck} label="SWMS Generated" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Users} label="Crew Briefed" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Wrench} label="Work in Progress" />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={CheckCircle2} label="Job Complete" />
            </div>
          </div>

          {/* Related Areas */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {relatedAreas.map((item, i) => (
              <div 
                key={i} 
                onClick={() => navigate(item.href)}
                className={`flex items-center gap-2 rounded-lg p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                  item.active 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <item.icon className={`w-5 h-5 ${item.active ? 'text-white' : `text-${item.color}-500`}`} />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Final CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              READY TO SIMPLIFY<br />
              <span className="text-blue-500">YOUR SAFETY DOCS?</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Smart tradies are already using 2Tap to stay compliant without the paperwork headache. Get your business set up now.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => setShowSetupModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm px-8 py-4 font-bold text-lg"
              >
                REGISTER INTEREST
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-950">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <button 
            onClick={() => setShowSetupModal(true)}
            className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
          >
            Want a custom 2Tap for your business or industry? <span className="underline">Let's talk →</span>
          </button>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 2Tap. Powered for Smart Business.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/features'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Features</button>
              <button onClick={() => setShowShareModal(true)} className="text-slate-400 hover:text-white text-sm transition-colors">Share</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Setup Modal */}
      <RequestSetupModal 
        isOpen={showSetupModal} 
        onClose={() => setShowSetupModal(false)} 
      />

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
      />
    </div>
  );
};

export default FeatureSWMS;
